/**
 * The summary of one part: model, fabric, size, comment and picture on the
 * left, the elements and options on the right.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  embroideryLabel,
  fabricCode,
  fixWithDots,
  modelImageUrl,
  partElements,
  partOptions,
  type OrderPart,
} from './dms-logic';

export function PartDetailsDialog({
  part,
  onClose,
}: Readonly<{ part: OrderPart; onClose: () => void }>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);
  const elements = partElements(part);
  const options = partOptions(part, t);
  const { selected } = part;
  const image = modelImageUrl(part);
  const hasEmbroidery =
    !!selected.embroideryType ||
    !!selected.embroideryPosition ||
    !!selected.embroideryText;
  const mainSize = String(selected.size ?? '') || '-';
  const size = selected.sizePants
    ? `${mainSize} - ${String(selected.sizePants)}`
    : mainSize;

  return (
    <dialog
      ref={ref}
      className="app-dialog part-details-dialog"
      aria-label={t('DMS_configurator.summary')}
      data-testid="mtm-part-details-dialog"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div className="part-details p-6">
        <button
          type="button"
          className="close p-2"
          aria-label="Close"
          data-testid="mtm-part-details-close-button"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <h4 className="text-center mb-4">{t('DMS_configurator.summary')}</h4>
        <div className="bs-row">
          <div className="col-span-6">
            <h5 className="underline">{t('DMS_orders.product')}</h5>
            <div className="bs-row mb-2">
              <div className="col-span-4">
                <div className="modal-label">MODEL</div>
                <div data-testid="mtm-part-details-model">
                  {part.model.data.model || '-'}
                </div>
              </div>
              <div className="col-span-4">
                <div className="modal-label">FABRICS</div>
                <div data-testid="mtm-part-details-fabric">
                  {fabricCode(part) || '-'}
                </div>
              </div>
              <div className="col-span-4">
                <div className="modal-label">SIZE</div>
                <div data-testid="mtm-part-details-size">{size}</div>
              </div>
            </div>
            <div className="mb-6">
              <div className="modal-label">COMMENT</div>
              <div data-testid="mtm-part-details-comment">
                {part.comment || '-'}
              </div>
            </div>
            {image && (
              <img
                src={image}
                alt=""
                className="part-model-image"
                data-testid="mtm-part-details-image"
              />
            )}
          </div>
          <div className="col-span-6">
            <h5 className="underline">{t('DMS_orders.element')}</h5>
            {elements.map((element) => (
              <div
                className="mb-2"
                key={element.label}
                data-testid="mtm-part-details-element"
                data-label={element.label}
              >
                <div>
                  <span className="modal-label">{element.label}</span>
                </div>
                <div data-testid="mtm-part-details-element-value">
                  {element.value}
                </div>
              </div>
            ))}
            {selected.embroideryType && (
              <div className="mb-2">
                <div>
                  <span className="modal-label">EMBROIDERY TYPE</span>
                </div>
                <div data-testid="mtm-part-details-embroidery-type">
                  {embroideryLabel(selected.embroideryType)}
                </div>
              </div>
            )}
            {selected.embroideryColor && (
              <div className="mb-2">
                <div>
                  <span className="modal-label">EMBROIDERY COLOR</span>
                </div>
                <div data-testid="mtm-part-details-embroidery-color">
                  {selected.embroideryColor}
                </div>
              </div>
            )}
            {selected.embroideryPosition && (
              <div className="mb-2">
                <div>
                  <span className="modal-label">EMBROIDERY POSITION</span>
                </div>
                <div data-testid="mtm-part-details-embroidery-position">
                  {selected.embroideryPosition}
                </div>
              </div>
            )}
            {selected.embroideryText && (
              <div className="mb-2">
                <div>
                  <span className="modal-label">EMBROIDERY TEXT</span>
                </div>
                <div data-testid="mtm-part-details-embroidery-text">
                  {fixWithDots(
                    selected.embroideryText,
                    selected.embroideryTextDots,
                  )}
                </div>
              </div>
            )}
            {elements.length === 0 && !hasEmbroidery && <div>No elements</div>}
            {options.length > 0 && (
              <h5 className="underline">{t('DMS_orders.options')}</h5>
            )}
            {options.map((option, index) => (
              <div
                className="mb-2"
                key={`${option}-${index}`}
                data-testid="mtm-part-details-option"
                data-value={option}
              >
                <div>
                  <span className="modal-label">{option}</span>
                </div>
                <div>YES</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </dialog>
  );
}
